import React, { useState, useEffect } from "react";
import { Typography, Grid, Card, Avatar, Box, Rating } from "@mui/material";
import { useParams } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { colors } from "../components/Theme/WebTheme";
import moment from "moment/min/moment-with-locales";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { makeStyles } from "@mui/styles";
import { SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import GoBackButton from "components/GoBackButton";
import { getLangKey } from "common/src/other/getLangKey";

const useStyles = makeStyles(() => ({
  pageContainer: {
    padding: "20px",
    backgroundColor: "#f7f9fc",
    minHeight: "100vh",
  },
  pageTitle: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#333",
    marginBottom: "20px",
    fontFamily: FONT_FAMILY,
  },
  card: {
    borderRadius: "16px",
    backgroundColor: colors.WHITE,
    boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.05)",
    padding: "24px",
    marginBottom: "24px",
  },
  cardHeader: {
    fontWeight: 600,
    fontSize: "20px",
    color: "#333",
    fontFamily: FONT_FAMILY,
    marginBottom: "24px", 
    textTransform: "capitalize",
    display: "block",
    width: "100%",
  },
  infoRow: {
    display: "flex",
    position: "relative",
  },
  horizontalDivider: {
    height: "1px",
    backgroundColor: "#eaedf3",
    width: "calc(50% - 15px)",
    margin: "0",
  },
  horizontalDividerContainer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
  infoCol: {
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0", 
    position: "relative",
  },
  verticalLine: {
    position: "absolute",
    width: "1px",
    backgroundColor: "#eaedf3",
    height: "100%",
    top: 0,
    left: "50%",
    zIndex: 1,
  },
  label: {
    color: "#64748b",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: FONT_FAMILY,
    textTransform: "capitalize",
  },
  value: {
    color: "#333",
    fontSize: "14px",
    fontWeight: 500,
    fontFamily: FONT_FAMILY,
    textAlign: "right",
  },
  statusBadge: {
    padding: "6px 12px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: 500,
    color: colors.WHITE,
    textAlign: "center",
    display: "inline-block",
  },
  profileSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "24px",
    marginTop: "12px",
  },
  avatar: {
    width: 80,
    height: 80,
    marginBottom: 10,
  },
  driverInfoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "14px 0",
    borderBottom: "1px solid #eaedf3",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  customerInfoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "14px 0",
    borderBottom: "1px solid #eaedf3",
    "&:last-child": {
      borderBottom: "none",
    },
  },
}));

function BookingDetails() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const settings = useSelector((state) => state.settingsdata.settings);
  const classes = useStyles();
  const role = useSelector((state) => state.auth.profile.usertype);
  const { state } = useLocation();

  useEffect(() => {
    if (bookinglistdata.bookings) {
      const booking = bookinglistdata.bookings.filter(
        (item) => item.id === id.toString()
      )[0];

      if (booking) {
        // Make sure id doesn't have a dash prefix if it exists
        if (booking.id && booking.id.startsWith('-')) {
          booking.id = booking.id.substring(1);
        }
        setData(booking);
      } else {
        // navigate("/404");
        setData([]);
      }
    }
  }, [bookinglistdata.bookings, id, navigate]);

  const getStatusBadge = (status) => {
    let bgColor = "#10b981"; 
    let displayText = status;
    
    if (status === "CANCELLED") {
      bgColor = "#ef4444"; 
      displayText = "Cancelled";
    } else if (status === "PENDING") {
      bgColor = "#f59e0b"; 
      displayText = "Pending";
    } else if (status === "COMPLETED") {
      displayText = "Completed";
    }
    
    return (
      <span 
        className={classes.statusBadge} 
        style={{ backgroundColor: bgColor }}
      >
        {displayText}
      </span>
    );
  };

  return (
    <Box className={classes.pageContainer} style={{ paddingTop: "20px" }}>
      {/* <GoBackButton
        isRTL={isRTL}
        onClick={() => {
          navigate("/bookings", { state: { pageNo: state?.pageNo } });
        }}
      /> */}

      <Typography variant="h1" className={classes.pageTitle} style={{ marginBottom: "30px" }}>
        {t("ride_details_page_title")}
      </Typography>

      <Grid container spacing={3}>
        {/* Ride Information and Driver Information */}
      <Grid item xs={12} md={8}>
          <Card className={classes.card} style={{ height: "auto", marginBottom: "10px" }}>
            <Typography className={classes.cardHeader} variant="h2">
              {t("ride_information")}
            </Typography>
            
            <Box sx={{ position: "relative" }}>
              <div className={classes.verticalLine}></div>
              
              <div className={classes.infoRow}>
                <div className={classes.infoCol} style={{ paddingRight: "12px" }}>
                  <Typography className={classes.label}>{t("booking_id")}</Typography>
                  <Typography className={classes.value}>{data?.id || ""}</Typography>
                </div>
                <div className={classes.infoCol} style={{ paddingLeft: "12px" }}>
                  <Typography className={classes.label}>{t("booking_ref")}</Typography>
                  <Typography className={classes.value}>{data?.reference || ""}</Typography>
                </div>
              </div>
              
              <div className={classes.horizontalDividerContainer}>
                <div className={classes.horizontalDivider}></div>
                <div className={classes.horizontalDivider}></div>
              </div>
              
              <div className={classes.infoRow}>
                <div className={classes.infoCol} style={{ paddingRight: "12px" }}>
                  <Typography className={classes.label}>{t("booking_status_web")}</Typography>
                  {data?.status ? getStatusBadge(data.status) : ""}
                </div>
                <div className={classes.infoCol} style={{ paddingLeft: "12px" }}>
                  <Typography className={classes.label}>{t("distance")}</Typography>
                  <Typography className={classes.value}>
                    {data?.distance 
                      ? (settings?.convert_to_mile 
                        ? `${data?.distance} ${t("mile")}` 
                        : `${data?.distance} ${t("km")}`)
                      : ""}
                  </Typography>
                </div>
              </div>
              
              <div className={classes.horizontalDividerContainer}>
                <div className={classes.horizontalDivider}></div>
                <div className={classes.horizontalDivider}></div>
              </div>
              
              <div className={classes.infoRow}>
                <div className={classes.infoCol} style={{ paddingRight: "12px" }}>
                  <Typography className={classes.label}>{t("you_rated_text")}</Typography>
                  <div className={classes.value}>
                    <Rating 
                      name="half-rating-read" 
                      value={data?.rating || 0}   
                      precision={0.5} 
                      readOnly 
                      size="small"
                    />
                  </div>
                </div>
                <div className={classes.infoCol} style={{ paddingLeft: "12px" }}>
                  <Typography className={classes.label}>{t("trip_start_date")}</Typography>
                  <Typography className={classes.value}>
                    {data?.tripdate ? moment(data?.tripdate).format("lll") : ""}
                  </Typography>
                </div>
              </div>
              
              <div className={classes.horizontalDividerContainer}>
                <div className={classes.horizontalDivider}></div>
                <div className={classes.horizontalDivider}></div>
              </div>
         
              <div className={classes.infoRow}>
                <div className={classes.infoCol} style={{ paddingRight: "12px" }}>
                  <Typography className={classes.label}>{t("pickup_address")}</Typography>
                  <Typography className={classes.value}>{data?.pickupAddress || ""}</Typography>
                </div>
                <div className={classes.infoCol} style={{ paddingLeft: "12px" }}>
                  <Typography className={classes.label}>{t("drop_address")}</Typography>
                  <Typography className={classes.value}>{data?.dropAddress || ""}</Typography>
                </div>
              </div>
              
              <div className={classes.horizontalDividerContainer}>
                <div className={classes.horizontalDivider}></div>
                <div className={classes.horizontalDivider}></div>
              </div>
              
              <div className={classes.infoRow}>
                <div className={classes.infoCol} style={{ paddingRight: "12px" }}>
                  <Typography className={classes.label}>{t("cancellation_reason")}</Typography>
                  <Typography className={classes.value}>
                    {data?.reason ? t(getLangKey(data?.reason)) : ""}
                  </Typography>
                </div>
                <div className={classes.infoCol} style={{ paddingLeft: "12px" }}>
                </div>
              </div>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card className={classes.card} style={{ height: "auto", marginBottom: "10px" }}>
            <Typography className={classes.cardHeader} variant="h2">
              {t("driver_info")}
            </Typography>
            <Box className={classes.profileSection}>
              <Avatar 
                src={data?.driver_image} 
                className={classes.avatar}
                alt={data?.driver_name || "Driver"}
              >
                <AccountCircleIcon style={{ fontSize: 40 }} />
              </Avatar>
            </Box>
            <Box>
              <div className={classes.driverInfoRow}>
                <Typography className={classes.label}>{t("name")}</Typography>
                <Typography className={classes.value}>{data?.driver_name || ""}</Typography>
              </div>
              <div className={classes.driverInfoRow}>
                <Typography className={classes.label}>{t("vehicle_type")}</Typography>
                <Typography className={classes.value}>{data?.carType ? t(getLangKey(data?.carType)) : ""}</Typography>
              </div>
              <div className={classes.driverInfoRow}>
                <Typography className={classes.label}>{t("cancellation_reason")}</Typography>
                <Typography className={classes.value}>
                  {data?.reason ? t(getLangKey(data?.reason)) : ""}
                </Typography>
              </div>
            </Box>
          </Card>
        </Grid>

        {/* Payment Information and Customer Information Section */}
        <Grid item xs={12} md={8}>
          <Card className={classes.card} style={{ height: "60%", marginTop: "0" }}>
            <Typography className={classes.cardHeader} variant="h2">
              {t("payment_info")}
            </Typography>
            
            <Box sx={{ position: "relative" }}>
              <div className={classes.verticalLine}></div>
              
              <div className={classes.infoRow}>
                <div className={classes.infoCol} style={{ paddingRight: "12px" }}>
                  <Typography className={classes.label}>{t("trip_cost")}</Typography>
                  <Typography className={classes.value}>
                    {(role === 'customer' && ['NEW','PAYMENT_PENDING'].indexOf(data?.status) !== -1) 
                      ? t('bid').toUpperCase() 
                      : (settings?.swipe_symbol 
                        ? `${data?.trip_cost || ""} ${settings?.symbol || ""}` 
                        : `${settings?.symbol || ""} ${data?.trip_cost || ""}`
                      )
                    }
                  </Typography>
                </div>
                <div className={classes.infoCol} style={{ paddingLeft: "12px" }}>
                  <Typography className={classes.label}>{t("convenience_fee")}</Typography>
                  <Typography className={classes.value}>
                    {(role === 'customer' && ['NEW','PAYMENT_PENDING'].indexOf(data?.status) !== -1) 
                      ? '--' 
                      : (settings?.swipe_symbol 
                        ? `${data?.convenience_fees || ""} ${settings?.symbol || ""}` 
                        : `${settings?.symbol || ""} ${data?.convenience_fees || ""}`
                      )
                    }
                  </Typography>
                </div>
              </div>
              
              <div className={classes.horizontalDividerContainer}>
                <div className={classes.horizontalDivider}></div>
                <div className={classes.horizontalDivider}></div>
              </div>
              
              <div className={classes.infoRow}>
                <div className={classes.infoCol} style={{ paddingRight: "12px" }}>
                  <Typography className={classes.label}>{t("discount_ammount")}</Typography>
                  <Typography className={classes.value}>
                    {settings?.swipe_symbol 
                      ? `${data?.discount || ""} ${settings?.symbol || ""}` 
                      : `${settings?.symbol || ""} ${data?.discount || ""}`
                    }
                  </Typography>
                </div>
                <div className={classes.infoCol} style={{ paddingLeft: "12px" }}>
                  <Typography className={classes.label}>{t("payment_mode")}</Typography>
                  <Typography className={classes.value}>{data?.payment_mode || ""}</Typography>
                </div>
              </div>
              
              <div className={classes.horizontalDividerContainer}>
                <div className={classes.horizontalDivider}></div>
                <div className={classes.horizontalDivider}></div>
              </div>
              
              <div className={classes.infoRow}>
                <div className={classes.infoCol} style={{ paddingRight: "12px" }}>
                  <Typography className={classes.label}>{t("driver_share")}</Typography>
                  <Typography className={classes.value}>
                    {(role === 'customer' && ['NEW','PAYMENT_PENDING'].indexOf(data?.status) !== -1) 
                      ? '--' 
                      : (settings?.swipe_symbol 
                        ? `${data?.driver_share || ""} ${settings?.symbol || ""}` 
                        : `${settings?.symbol || ""} ${data?.driver_share || ""}`
                      )
                    }
                  </Typography>
                </div>
                <div className={classes.infoCol} style={{ paddingLeft: "12px" }}>
                  {/* Empty column to maintain layout */}
                </div>
              </div>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card className={classes.card} style={{ height: "auto" }}>
            <Typography className={classes.cardHeader}>
              {t("customer_info")}
            </Typography>
            <Box className={classes.profileSection}>
              <Avatar 
                src={data?.customer_image} 
                className={classes.avatar}
                alt={data?.customer_name}
              >
                <AccountCircleIcon style={{ fontSize: 40 }} />
              </Avatar>
            </Box>
            <Box>
              <div className={classes.customerInfoRow}>
                <Typography className={classes.label}>{t("id")}</Typography>
                <Typography className={classes.value}>{data?.customer}</Typography>
              </div>
              <div className={classes.customerInfoRow}>
                <Typography className={classes.label}>{t("name")}</Typography>
                <Typography className={classes.value}>{data?.customer_name}</Typography>
              </div>
              <div className={classes.customerInfoRow}>
                <Typography className={classes.label}>{t("contact")}</Typography>
                <Typography className={classes.value}>
                  {data?.customer_contact 
                    ? (settings?.AllowCriticalEditsAdmin 
                      ? data?.customer_contact 
                      : t("hidden_demo")
                    )
                    : ""}
                </Typography>
              </div>
              <div className={classes.customerInfoRow}>
                <Typography className={classes.label}>{t("email")}</Typography>
                <Typography className={classes.value}>
                  {data?.customer_email 
                    ? (settings?.AllowCriticalEditsAdmin 
                      ? data?.customer_email 
                      : t("hidden_demo")
                    )
                    : ""}
                </Typography>
              </div>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default BookingDetails;